import React, { useEffect, useRef, useState } from "react";
import { CloudUpload, DatabaseBackup, FileUp, Loader } from "lucide-react";
import Spacing from "../Spacing";
import Card from "../Card";
import Select from "react-select";
import { getPractice, notify } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import ConfirmationFileUploadModal from "../Modal/ConfirmationFileUploadModal";
import { syncIpaData, updateIpaData } from "../../services/workerServices";
import IPAImportViewModal from "../Modal/IPAImportViewModal";
import { Config } from "../../Config";

export default function IPADataImport({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    account_id = selectedPracticeChange?.account_id;
    location_id = selectedPracticeChange?.location_id;
  }

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    insurance_type: "",
    file: null,
  };

  const [formData, setFormData] = useState(defaultValues);
  const [uploadIpaDisabled, setUploadIpaDisabled] = useState(true);
  const [uploadIpaLoader, setUploadIpaLoader] = useState(false);
  const [ipaFile, setIpaFile] = useState(null);
  const [fileIpaError, setIpaFileError] = useState(false);
  const [ipaFileUploadConfirmation, setIpaFileUploadConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const normalizeText = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try { return JSON.stringify(v); } catch { return String(v); }
  };
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [allIpaData, setAllIpaData] = useState([]);
  const [viewIpaDataInfo, setViewIpaDataInfo] = useState(false);
  const [ipaEditLoader, setIpaEditLoader] = useState(false);

  // WebSocket (IPA update progress)
  const ipaWsRef = useRef(null);
  const [ipaUpdateToken, setIpaUpdateToken] = useState(null);
  const [ipaUpdateLogs, setIpaUpdateLogs] = useState([]);
  const [ipaUpdateWsActive, setIpaUpdateWsActive] = useState(false);
  const [ipaUpdateWsDone, setIpaUpdateWsDone] = useState(false);

  const [insuranceType] = useState([
    { value: "Medicaid", label: "Medicaid" },
  ]);

  const extractToken = (resp) => {
    // supports either full response object or a direct token string
    if (!resp) return null;
    if (typeof resp === "string") return resp;
    return (
      resp?.data?.ws_token ||
      resp?.data?.token ||
      resp?.data?.data?.ws_token ||
      resp?.data?.data?.token ||
      resp?.data?.data?.update_token ||
      resp?.ws_token ||
      resp?.token ||
      null
    );
  };

  const closeIpaWs = () => {
    try {
      if (ipaWsRef.current) {
        ipaWsRef.current.onopen = null;
        ipaWsRef.current.onmessage = null;
        ipaWsRef.current.onerror = null;
        ipaWsRef.current.onclose = null;
        ipaWsRef.current.close();
      }
    } catch (e) {
      // ignore
    } finally {
      ipaWsRef.current = null;
    }
  };

  const startIpaWs = (token) => {
    if (!token) return;

    // reset state for fresh run
    setIpaUpdateLogs([]);
    setIpaUpdateWsActive(true);
    setIpaUpdateWsDone(false);
    closeIpaWs();

    let ipaWsURL = `${Config.apiServerUrl}/ipa/ws?account_id=${account_id}&ws_token=${encodeURIComponent(token)}`;
    ipaWsURL = ipaWsURL.replace(/^http(s?):/i, "ws$1:");
    if (ipaWsURL.startsWith("//")) {
      ipaWsURL = `${window.location.protocol === "https:" ? "wss:" : "ws:"}${ipaWsURL}`;
    }

    const wsUrl = ipaWsURL;

    try {
      const ws = new WebSocket(wsUrl);
      ipaWsRef.current = ws;

      ws.onopen = () => {
        // no UI log needed on connect
      };

      ws.onmessage = (evt) => {
        const raw = typeof evt?.data === "string" ? evt.data : JSON.stringify(evt?.data);
        setIpaUpdateLogs((prev) => [...prev, raw]);

        // done detection (supports JSON or plain text)
        try {
          const j = JSON.parse(raw);
          const done =
            j?.done === true ||
            j?.status === "done" ||
            j?.status === "completed" ||
            j?.completed === true;
          if (done) {
            setIpaUpdateWsDone(true);
            setIpaUpdateWsActive(false);
            closeIpaWs();
          }
        } catch {
          const t = String(raw).toLowerCase();
          if (t.includes("done") || t.includes("completed") || t.includes("finished")) {
            setIpaUpdateWsDone(true);
            setIpaUpdateWsActive(false);
            closeIpaWs();
          }
        }
      };

      ws.onerror = () => {
        setIpaUpdateLogs((prev) => [...prev, "{\"status\":\"error\",\"details\":\"WebSocket error\"}"]);
      };

      ws.onclose = (e) => {
        setIpaUpdateLogs((prev) => [...prev, `WS: closed (code=${e?.code || ""})`]);
        setIpaUpdateWsActive(false);
      };
    } catch (e) {
      setIpaUpdateLogs((prev) => [...prev, `WS init failed: ${String(e?.message || e)}`]);
      setIpaUpdateWsActive(false);
    }
  };

  // cleanup WS on unmount
  useEffect(() => {
    return () => closeIpaWs();
  }, []);

  // when modal closes, cleanup ws + token
  useEffect(() => {
    if (!viewIpaDataInfo) {
      closeIpaWs();
      setIpaUpdateToken(null);
      setIpaUpdateLogs([]);
      setIpaUpdateWsActive(false);
      setIpaUpdateWsDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIpaDataInfo]);

  // auto close modal after ws done (optional)
  useEffect(() => {
    if (!ipaUpdateWsDone) return;
    const t = setTimeout(() => {
      setAllIpaData([]);
      setViewIpaDataInfo(false);
      window.scrollTo(0, previousScrollPosition || 0);
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipaUpdateWsDone]);

  useEffect(() => {
    setFormData({
      ...formData,
      account_id: account_id,
      location_id: location_id,
      insurance_type: "",
      file: null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleIpaFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (
      selectedFile &&
      ![
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
        "application/csv",
      ].includes(selectedFile.type)
    ) {
      setIpaFileError(true);
      setUploadIpaDisabled(true);
      setIpaFile(null);
      setFormData({
        ...formData,
        file: null
      });
    } else {
      setIpaFileError(false);
      setUploadIpaDisabled(false);
      setIpaFile(selectedFile);
      setFormData({
        ...formData,
        file: selectedFile
      });
    }
  };

  const handelShowWarning = async (e) => {
    e.preventDefault();
    setIpaFileUploadConfirmation(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handelSubmitIpaData = async (e) => {
    setUploadIpaDisabled(true);
    setUploadIpaLoader(true);

    // fresh sync → reset ws state
    setIpaUpdateToken(null);
    setIpaUpdateLogs([]);
    setIpaUpdateWsActive(false);
    setIpaUpdateWsDone(false);

    try {
      const response = await syncIpaData(formData);
      
      if (response?.detail) {
        setErrorMessage(normalizeText(response?.detail));
      } else { 
        if(response?.data?.status === 'success'){
          notify('success', response?.data?.message);

          const token = extractToken(response?.data?.ws_token);
          setIpaUpdateToken(token);

          setAllIpaData(response?.data?.data)
          setViewIpaDataInfo(true);
          setPreviousScrollPosition(window.scrollY);
          window.scrollTo(0, 0);
          setIpaFileUploadConfirmation(false);
          setIpaFile(null);
          setErrorMessage(normalizeText(null));
          setFormData(defaultValues);
        } else {
          setErrorMessage(normalizeText(response?.data?.message));
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        setErrorMessage(normalizeText(error?.response?.data?.detail));
      }
    } finally { 
      setUploadIpaDisabled(false);
      setUploadIpaLoader(false);
    }
  };

  const handelUpdateIpaData = async (data) => {
    setIpaEditLoader(true);

    try {
      const response = await updateIpaData(data);
      
      if (response?.detail) {
        notify("error", normalizeText(response?.detail));
      } else { 
        if(response?.data?.status === 'success'){
          notify("success", normalizeText(response?.data?.message, 7));

          // ✅ connect WS only after UPDATE success, using token stored from SYNC call
          if (ipaUpdateToken) {
            startIpaWs(ipaUpdateToken);
          } else {
            // fallback if token is missing
            setAllIpaData([]);
            setViewIpaDataInfo(false);
            window.scrollTo(0, previousScrollPosition);
          }
        } else {
          notify("error", normalizeText(response?.data?.message));
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", normalizeText(error?.response?.data?.detail));
      }
    } finally { 
      setIpaEditLoader(false);
    }
  };

  useDocumentTitle("IPA Data Sync");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          <i className="docpro_white_color" style={{ backgroundColor: "#0b68cb" }}>
            <FileUp width={18} height={18} />
          </i>
          IPA Data Sync
        </h2>
      </div>
      <Spacing lg="15" sm="15" />

      <div className="flex items-center" style={{ justifyContent: "center" }}>
        <div className="docpro_sync_data_section docpro_settings_container w-full">
          <Card variant="docpro_card w-full">
            <form onSubmit={handelShowWarning} className="docpro_text_start">
              <Spacing lg="10" sm="10" />
              <div className="card_body">
                <label className="docpro_fw_500 docpro_primary_color">Insurance Type</label>
                <Select
                  name="insurance_type"
                  options={insuranceType}
                  onChange={(selectedOption) => {
                    setFormData({ ...formData, insurance_type: selectedOption.value });
                    setUploadIpaDisabled(false);
                  }}
                  placeholder="Search..."
                  isSearchable={true}
                  value={insuranceType.find((option) => option.value === formData.insurance_type)}
                  className="react-select-container w-full"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "43px",
                      borderRadius: "8px",
                      borderColor: "var(--border)",
                    }),
                  }}
                />

                <Spacing lg="20" sm="20" />
                <label className="docpro_primary_color">
                  <span className="docpro_fw_500">IPA Data File</span>{" "}
                  <small className="docpro_red_color">(.xlsx, .xls, .csv)</small>
                </label>

                <div
                  className="docpro_input_file_container"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add("dragover");
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("dragover")}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("dragover");
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleIpaFileChange({ target: { files: [f] } });
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload Excel/CSV file by dragging or clicking"
                >
                  <span className="docpro_file_icon" aria-hidden="true">
                    <CloudUpload height={28} width={28} />
                  </span>
                  <div className="docpro_file_text">
                    <div className="docpro_file_title">{ipaFile ? ipaFile.name : "Drag and drop IPA data file"}</div>
                    <div className="docpro_file_hint">{ipaFile ? "" : "here, or click to upload"}</div>
                  </div>

                  <input type="file" accept=".xlsx,.xls,.csv" className="docpro_file_hidden_input" onChange={handleIpaFileChange} />
                </div>

                {fileIpaError && <p className="docpro_error_message">Please upload a valid Excel/CSV file.</p>}
              </div>

              <Spacing lg="25" sm="25" />
              <button
                type="submit"
                className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg w-full"
                style={{ padding: "12px 13px" }}
                disabled={uploadIpaDisabled}
              >
                <DatabaseBackup width={20} height={20} />
                <span>Sync IPA Data</span>
                {uploadIpaLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
              <Spacing lg="3" sm="3" />
            </form>
          </Card>
        </div>
      </div>

      {ipaFileUploadConfirmation && (
        <ConfirmationFileUploadModal
          openModal={ipaFileUploadConfirmation}
          setOpenModal={setIpaFileUploadConfirmation}
          previousScrollPosition={previousScrollPosition}
          uploadDisabled={uploadIpaDisabled}
          uploadLoader={uploadIpaLoader}
          modalAction={handelSubmitIpaData}
          errorMessage={normalizeText(errorMessage)}
          setErrorMessage={setErrorMessage}
          fileName={ipaFile}
          uploadType="ipa"
        />
      )}

      {viewIpaDataInfo && (
        <IPAImportViewModal 
          openModal={viewIpaDataInfo}
          setOpenModal={setViewIpaDataInfo}
          previousScrollPosition={previousScrollPosition}
          enableLoader={ipaEditLoader}
          modalAction={handelUpdateIpaData}
          data={allIpaData}
          account_id={account_id}
          location_id={location_id}
          setAllIpaData={setAllIpaData}
          setIpaFile={setIpaFile}
          wsActive={ipaUpdateWsActive}
          wsDone={ipaUpdateWsDone}
          wsLogs={ipaUpdateLogs}
        />
      )}
    </>
  );
}